"use client"
// PROTOTYPE — throwaway. Variant C: "Chrome-less". See NOTES.md.
//
// The argument: the card is decoration. Strip the container entirely and let type,
// space and a single accent do the work. The wordmark becomes a real page header
// rather than an ornament inside a box, the sign-up link moves up into that header
// (it's navigation, not part of the form), fields are underlines rather than boxes,
// and the work-account options sit at the bottom as quiet links.

import React from "react"
import Image from "next/image"
import Link from "next/link"
import { ArrowRight } from "lucide-react"
import { BRAND, BRAND_DARK, GoogleIcon, MicrosoftIcon, Spinner, type SignInViewProps } from "./shell"

export function VariantC({
  email, setEmail, password, setPassword, loading, error, message, onSubmit, onOAuth,
}: SignInViewProps) {
  const field =
    "w-full border-0 border-b border-slate-300 bg-transparent px-0 py-2.5 text-[16px] text-slate-900 placeholder:text-slate-300 outline-none transition-colors focus:border-[var(--brand)] focus:ring-0"

  const oauthLink =
    "inline-flex items-center gap-2 text-[13px] font-medium text-slate-600 underline-offset-4 transition-colors hover:text-[var(--brand)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]/40 focus-visible:ring-offset-2 rounded"

  return (
    <div
      style={{ ["--brand" as string]: BRAND, ["--brand-dark" as string]: BRAND_DARK }}
      className="flex min-h-screen flex-col bg-white"
    >
      {/* Page header — wordmark as masthead, sign-up as navigation. */}
      <header className="flex items-center justify-between px-6 py-6 lg:px-12">
        <Image
          src="/partnr-wordmark.png"
          alt="Partnr"
          width={800}
          height={280}
          priority
          className="h-auto w-[112px]"
        />
        <p className="text-[13px] text-slate-500">
          No account?{" "}
          <Link href="/auth/signup" className="font-semibold text-[var(--brand)] hover:underline">
            Sign up
          </Link>
        </p>
      </header>

      <main className="flex flex-1 items-center px-6 lg:px-12">
        <div className="mx-auto w-full max-w-[360px] pb-24">
          <h1 className="text-[32px] font-bold leading-tight tracking-tight text-slate-900">Sign in</h1>
          <p className="mt-2.5 text-[14px] text-slate-500">
            Use your email, or continue with a work account below.
          </p>

          {message && (
            <div className="mt-7 border-l-2 border-emerald-500 bg-emerald-50/60 px-3.5 py-3 text-[13px] text-emerald-800">
              {message}
            </div>
          )}
          {error && (
            <div className="mt-7 border-l-2 border-red-500 bg-red-50/60 px-3.5 py-3 text-[13px] text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-9 space-y-7">
            <div>
              <label htmlFor="c-email" className="mb-1 block text-[12px] font-semibold uppercase tracking-wider text-slate-400">
                Email
              </label>
              <input
                id="c-email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className={field}
                placeholder="you@company.com"
                required
              />
            </div>
            <div>
              <label htmlFor="c-password" className="mb-1 block text-[12px] font-semibold uppercase tracking-wider text-slate-400">
                Password
              </label>
              <input
                id="c-password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className={field}
                placeholder="••••••••"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="group flex w-full items-center justify-center gap-2 rounded-full bg-[var(--brand)] px-5 py-3.5 text-[14px] font-semibold text-white transition-colors hover:bg-[var(--brand-dark)] disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]/40 focus-visible:ring-offset-2"
            >
              {loading ? <Spinner /> : null}
              {loading ? "Signing in…" : "Sign in"}
              {!loading && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />}
            </button>
          </form>

          <div className="mt-10 flex items-center gap-6 border-t border-slate-200 pt-6">
            <button type="button" onClick={() => onOAuth("google")} className={oauthLink}>
              <GoogleIcon className="h-4 w-4" />
              Google
            </button>
            <button type="button" onClick={() => onOAuth("microsoft")} className={oauthLink}>
              <MicrosoftIcon className="h-4 w-4" />
              Microsoft
            </button>
          </div>
        </div>
      </main>
    </div>
  )
}
